import { useLocation, navigate } from "@reach/router";
import { cn } from "@babybjorn/ui";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useEffect, useRef, type FC } from "react";
import type { emptyCartContent } from "../../../containers/header/cart.container";
import Button from "../Button";
import Icon from "../Icon";
import { isValidIcon } from "../Icon/Icon";
import ModalCartItem, {
	type ModalCartItemProps,
} from "../ModalCartItem/ModalCartItem";
import * as s from "./CartModal.module.scss";
import { useProductGiftMessage } from "../../../hooks/use-product-gift-message";
import { useProductGiftBox } from "../../../hooks/use-product-gift-box";
import { useAppState } from "../../../context/app/context";
import { CartRecommendations } from "../../CartRecommendations";
import { pushCustomGTMEvent } from "../../../utils/gtm/gtm-handlers";

type CartModalProps = {
	overlayOnClick: () => void;
	buttonOnClick: () => void;
	emptyCartData: emptyCartContent;
	heading?: string;
	closeButtonLabel: string;
	cartItems?: ModalCartItemProps[];
	totalAmount: string;
	freeShippingLabel: string;
	disabled?: boolean;
	checkoutLink: string;
};

const CartModal: FC<CartModalProps> = ({
	buttonOnClick,
	overlayOnClick,
	emptyCartData,
	heading,
	closeButtonLabel,
	cartItems,
	totalAmount,
	freeShippingLabel,
	checkoutLink,
	disabled = false,
}) => {
	const { giftMessageProduct } = useProductGiftMessage();
	const { giftBoxProduct } = useProductGiftBox();
	const { siteConfig } = useAppState();

	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const setMeasuredHeight = () => {
			if (ref.current)
				ref.current.style.setProperty(
					"--doc-height",
					`${window.innerHeight}px`,
				);
		};

		window.addEventListener("resize", setMeasuredHeight);
		setMeasuredHeight();
		return () => window.removeEventListener("resize", setMeasuredHeight);
	}, []);
	const location = useLocation();

	const cartPage = location.pathname.split("/").find(Boolean) === "cart";

	const giftMessageTitle = giftMessageProduct?.name[siteConfig.language];
	const giftMessageDescription =
		giftMessageProduct?.description?.[siteConfig.language];
	const giftBoxTitle = giftBoxProduct?.description?.[siteConfig.language];

	const handleButtonClick = () => {
		if (cartPage) {
			buttonOnClick();
		} else {
			pushCustomGTMEvent({
				type: "goToCart",
			});
			void navigate(checkoutLink);
		}
	};

	return (
		<div className={s.modalWrapper} ref={ref}>
			<div
				className={s.clickableOverlay}
				onClick={overlayOnClick}
				onKeyDown={overlayOnClick}
				role="presentation"
			/>
			<aside
				className={cn(s.modal, { [s.emptyCart]: cartItems?.length === 0 })}
			>
				{cartItems?.length === 0 && (
					<div className={s.banner}>
						<div className={s.bannerContent}>
							<div>
								<Icon
									icon={emptyCartData.bannerIcon}
									width={24}
									color="#FFFFFF"
								/>
							</div>
							{emptyCartData.bannerText && (
								<span>{emptyCartData.bannerText}</span>
							)}
						</div>
						<Button onClick={buttonOnClick} icon="close" buttonType="icon" />
					</div>
				)}
				<section className={s.modalContent}>
					<div
						className={cn(s.headerWrapper, {
							[s.withCartItems]: cartItems && cartItems.length > 0,
						})}
					>
						{cartItems?.length === 0 ? (
							<div className={s.imageContainer}>
								{emptyCartData.backgroundImage}
								<div className={s.textOnImageContainer}>
									<div>
										<div>
											<h3>{emptyCartData.title}</h3>
											<span>{emptyCartData.description}</span>
										</div>
										{(emptyCartData.buttonHref ||
											emptyCartData.buttonLabel) && (
											<Button
												variant="inverted"
												label={emptyCartData.buttonLabel}
												href={emptyCartData.buttonHref}
												icon={
													isValidIcon(emptyCartData.buttonIcon)
														? emptyCartData.buttonIcon
														: undefined
												}
											/>
										)}
									</div>
								</div>
							</div>
						) : (
							<>
								<Button
									onClick={buttonOnClick}
									variant="noBg"
									iconLeft
									icon="close"
									buttonType="standard"
									label={closeButtonLabel}
									noPaddingInline
								/>
								<h3>
									{heading}
									{` (${cartItems?.reduce((t, i) => t + (i.quantity || 0), 0)})`}
								</h3>
							</>
						)}
					</div>
					{cartItems && (
						<ul className={s.modalList}>
							{cartItems.map((item, index) => (
								<ModalCartItem
									key={index}
									disabled={disabled}
									productName={
										item.productKey === "gift-message"
											? (giftMessageTitle as string)
											: item.productName
									}
									productImage={item.productImage}
									variantLabel={
										item.productKey === "gift-message"
											? giftMessageDescription
											: item.productKey === "gift-box"
												? giftBoxTitle
												: item.variantLabel
									}
									price={item.price}
									quantity={item.quantity}
									quantityLabel={item.quantityLabel}
									increment={item.increment}
									decrement={item.decrement}
									incrementLabel={t("increment-button")}
									decrementLabel={t("decrement-button")}
									remove={item.remove}
									productLink={item.productLink}
									enableQuantityCounter={item.enableQuantityCounter}
									enableCloseButton={item.enableCloseButton}
								/>
							))}
						</ul>
					)}
				</section>

				<CartRecommendations className={s.modalFooter} amount={1} cartModal />

				{cartItems && cartItems.length > 0 && (
					<div className={s.asideFooter}>
						<div>
							<p>{`${t("Subtotal")}: ${totalAmount}`}</p>
							{freeShippingLabel && <span>{freeShippingLabel}</span>}
						</div>
						<Button
							variant="primary"
							icon="cart"
							label={t("Go to cart")}
							onClick={handleButtonClick}
							fullWidth
							className={cn(s.checkoutBtn, { [s.disabled]: disabled })}
						/>
					</div>
				)}
			</aside>
		</div>
	);
};

export default CartModal;
